import React, { useCallback } from 'react'
import { useAppSelector } from '@verzekeringapp-mono/shared/src/hooks/state'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getResourceAuthorization } from '@verzekeringapp-mono/shared/src/state/selectors/resource_authorization'
import { getCombiCollectionAmount } from '@verzekeringapp-mono/shared/src/state/selectors/policies'
import { getCombiPolicyPdfUrl } from '@verzekeringapp-mono/shared/src/api/policies'

const MyPoliciesHeader: React.FC = () => {
  const resourceAuthorization = useAppSelector(getResourceAuthorization)
  const combiCollectionAmount = useAppSelector(getCombiCollectionAmount)
  const relationInfo = resourceAuthorization?.relationInfo
  const [gettingPolicyUrl, setGettingPolicyUrl] = React.useState(false)
  const [downloadError, setDownloadError] = React.useState(false)
  const canDownloadPolicy = !!(relationInfo?.relationNumber && relationInfo?.agentNumber)

  const onPolicyStatementPress = useCallback(async () => {
    if (!relationInfo?.relationNumber || !relationInfo?.agentNumber) return

    setGettingPolicyUrl(true)
    setDownloadError(false)
    try {
      const response = await getCombiPolicyPdfUrl(relationInfo.agentNumber, relationInfo.relationNumber)

      window.open(response.data.url, '_blank', 'noopener,noreferrer')
    } catch {
      setDownloadError(true)
    } finally {
      setGettingPolicyUrl(false)
    }
  }, [relationInfo])

  return (
    <>
      <div className='flex flex-wrap items-start justify-between gap-4 pb-5'>
        <div>
          <h1 className='font-semibold'>Mijn verzekeringen</h1>

          <p className='mt-1 text-sm'>Combinummer: {resourceAuthorization?.combiNumber}</p>
        </div>

        <div className='ml-auto text-right'>
          <p className='font-semibold text-text-brand'>
            {Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(Number(combiCollectionAmount))}
          </p>

          <p className='mt-1 text-sm'>
            {relationInfo?.paymentPeriod && `per ${relationInfo.paymentPeriod.toLowerCase()}`}

            {!!relationInfo?.combiPackageDiscount && (
              <>
                {relationInfo.paymentPeriod && ', '}

                incl. {Intl.NumberFormat('nl-NL', { style: 'percent' }).format(Number(relationInfo.combiPackageDiscount) / 100)} korting
              </>
            )}
          </p>
        </div>
      </div>

      <div className='grid grid-cols-2 gap-x-4 gap-y-5 border-t border-border-subtle py-6 sm:grid-cols-3'>
        <div>
          <p className='text-sm'>Ingangsdatum:</p>

          <p className='text-sm'>{relationInfo?.combiStartingDate}</p>
        </div>

        <div>
          <p className='text-sm'>Vervaldatum:</p>

          <p className='text-sm'>{relationInfo?.combiContractExpirationDate}</p>
        </div>

        <div>
          <p className='text-sm'>Polisblad</p>

          <button
            type='button'
            aria-label='Polisblad downloaden'
            disabled={gettingPolicyUrl || !canDownloadPolicy}
            onClick={onPolicyStatementPress}
            className='flex items-center gap-1 text-sm disabled:cursor-not-allowed disabled:opacity-50'
          >
            <FontAwesomeIcon
              icon={['fas', 'arrow-right']}
              className='text-icon-brand'
            />

            <span className='text-sm underline'>{gettingPolicyUrl ? 'Ophalen…' : 'Downloaden'}</span>
          </button>
        </div>

        <div>
          <p className='text-sm'>Actieve polissen:</p>

          <p className='text-sm'>{relationInfo?.combiNumberOfPolicies}</p>
        </div>

        <div>
          <p className='text-sm'>Betaalmethode:</p>

          <p className='text-sm'>{relationInfo?.paymentMethod}</p>
        </div>

        {relationInfo?.combiPolicyCondition?.url && (
          <div>
            <p className='text-sm'>Algemene voorwaarden</p>

            <a
              href={relationInfo.combiPolicyCondition.url}
              target='_blank'
              rel='noreferrer'
              aria-label='Algemene voorwaarden downloaden'
              className='flex items-center gap-1 text-sm'
            >
              <FontAwesomeIcon
                icon={['fas', 'arrow-right']}
                className='text-icon-brand'
              />

              <span className='text-sm underline'>Downloaden</span>
            </a>
          </div>
        )}
      </div>

      {downloadError && (
        <p
          role='alert'
          className='mb-4 text-sm text-status-danger-foreground'
        >
          Het polisblad kon niet worden opgehaald. Probeer het opnieuw.
        </p>
      )}
    </>
  )
}

export default MyPoliciesHeader
